import { FC, useState } from 'react';
import { Button, Modal, IconButton, Dropdown, Radio } from 'components/core';
import { Styled } from '.';
import { useFetchScannerDetails } from 'reactQuery/queries/useFetchScannerDetails';
import { ITeroScannerConfigurationProps } from '.';
import { LoadingSpinner } from 'styles/sharedStyles';
import { useAddIteroLineItem } from 'reactQuery/mutations';
import { buildQuantityOptions } from 'components/ProductDetails';
import { AddToCartConfirmationModal } from 'components/AddToCartConfirmationModal';
import { useI18n } from 'contexts/i18n';

const ITeroScannerConfiguration: FC<ITeroScannerConfigurationProps> = ({
  product,
}) => {
  const [selectedScannerType, setSelectedScannerType] = useState('');

  const [selectedServicePeriodOption, setSelectedServicePeriodOption] =
    useState('');

  const [displayPrice, setDisplayPrice] = useState('');

  const [selectedPrice, setSelectedPrice] = useState('');

  const [selectedServicePlan, setSelectedServicePlan] = useState('1month');

  const { data, isLoading } = useFetchScannerDetails('', product.sku);

  const addScannerMutation = useAddIteroLineItem();

  const [allowableQuantities, setAllowableQuantities] = useState(
    buildQuantityOptions(product.quantityLimit)
  );
  const [quantity, setQuantity] = useState(allowableQuantities[0]?.value);
  const [showAddToCartConfirmation, setShowAddToCartConfirmation] =
    useState(false);
  const i18n = useI18n();

  if (isLoading) {
    return (
      <Styled.Container>
        <Styled.LoadingWrapper>
          <LoadingSpinner isPrimary={true} width="20px" height="20px" />
          {i18n['Loading Scanner Details']}...
        </Styled.LoadingWrapper>
      </Styled.Container>
    );
  }

  const handleScannerTypeChange = (value: string) => {
    setSelectedScannerType(value);
  };
  const handleAddToCart = () => {
    const qty = parseInt(quantity);
    addScannerMutation.mutate({
      variantId: product.variantId,
      quantity: qty,
      scannerType: selectedScannerType,
      scannerServiceType: selectedServicePeriodOption,
      itemPrice: selectedPrice,
    });
  };

  const scannerTypes: { label: string; value: string }[] = [];
  data?.forEach(scanner => {
    scannerTypes.push({
      label: scanner.scannerType,
      value: scanner.scannerType,
    });
  });

  let servicePeriodOptions: any = [];

  data?.forEach(scanner => {
    if (scanner.scannerType === selectedScannerType) {
      servicePeriodOptions = scanner.serviceOptions;
    }
  });

  const onCloseAddToCartConfirmation = () => {
    setShowAddToCartConfirmation(false);
  };

  return (
    <Styled.Container>
      <Styled.FormElementWrapper data-testid="scanner-quantity">
        <Styled.Title>{i18n['Quantity']}:</Styled.Title>
        <Dropdown
          options={allowableQuantities}
          onChange={val => {
            setQuantity(val);
          }}
          selectedValue={quantity}
          label="Qty"
          size="sm"
        ></Dropdown>
      </Styled.FormElementWrapper>
      <Styled.FormElementWrapper data-testid="scanner-type">
        <Styled.Title>{i18n['Scanner Type']}:</Styled.Title>
        <Dropdown
          options={scannerTypes}
          onChange={val => {
            handleScannerTypeChange(val);
          }}
          selectedValue={''}
          label="Scanner Type"
        ></Dropdown>
      </Styled.FormElementWrapper>
      {selectedScannerType && (
        <Styled.FormElementWrapper>
          <Styled.Title>{i18n['Service Term']}:</Styled.Title>
          <Styled.ServicePeriodList>
            {servicePeriodOptions.map((option: any) => {
              return (
                <Styled.ServicePeriodListItem
                  isSelected={option.period === selectedServicePeriodOption}
                  onClick={() => {
                    setSelectedServicePeriodOption(option.period);

                    const selectedDisplayPrice = servicePeriodOptions.filter(
                      (spo: any) => spo.period === option.period
                    )[0].displayPrice;

                    const selectedPrice = servicePeriodOptions.filter(
                      (spo: any) => spo.period === option.period
                    )[0].price;

                    setDisplayPrice(selectedDisplayPrice);
                    setSelectedPrice(selectedPrice);
                  }}
                >
                  <Radio
                    checked={option.period === selectedServicePeriodOption}
                    onChange={() => {
                      setSelectedServicePeriodOption(option.period);

                      const selectedDisplayPrice = servicePeriodOptions.filter(
                        (spo: any) => spo.period === option.period
                      )[0].price;
                      const selectedPrice = servicePeriodOptions.filter(
                        (spo: any) => spo.period === option.period
                      )[0].price;

                      setDisplayPrice(selectedDisplayPrice);
                      setSelectedPrice(selectedPrice);
                    }}
                    label={option.periodWithPrice}
                  />
                </Styled.ServicePeriodListItem>
              );
            })}
          </Styled.ServicePeriodList>
        </Styled.FormElementWrapper>
      )}
      {selectedPrice && (
        <Styled.FormElementWrapper>
          <Styled.Title>{i18n['Service Plan']}:</Styled.Title>
          <Styled.ServicePlanList>
            <Styled.ServicePlanListItemContainer>
              <Styled.ServicePlanListItem
                isSelected={selectedServicePlan === '1month'}
                onClick={() => {
                  setSelectedServicePlan('1month');
                }}
              >
                <Radio
                  checked={selectedServicePlan === '1month'}
                  onChange={() => {
                    setSelectedServicePlan('1month');
                  }}
                  label={i18n['Monthly']}
                />
              </Styled.ServicePlanListItem>
              <Styled.ServicePlanListItem
                isSelected={selectedServicePlan === '1year'}
                onClick={() => {
                  setSelectedServicePlan('1year');
                }}
              >
                <Radio
                  checked={selectedServicePlan === '1year'}
                  onChange={() => {
                    setSelectedServicePlan('1year');
                  }}
                  label={i18n['Yearly']}
                />
              </Styled.ServicePlanListItem>
            </Styled.ServicePlanListItemContainer>
          </Styled.ServicePlanList>
        </Styled.FormElementWrapper>
      )}

      {showAddToCartConfirmation && (
        <AddToCartConfirmationModal
          name={product.name}
          price={product.displayPrice}
          imageUrl={product.images[0].thumbnail}
          onClose={onCloseAddToCartConfirmation}
        />
      )}

      <Styled.QuoteButtonContainer>
        <Button
          width="100%"
          size="md"
          onClick={handleAddToCart}
          loading={addScannerMutation.isLoading}
          disabled={
            !selectedScannerType ||
            !selectedServicePeriodOption ||
            !selectedServicePlan
          }
        >
          {i18n['Add To Cart']}
        </Button>
      </Styled.QuoteButtonContainer>
    </Styled.Container>
  );
};

export default ITeroScannerConfiguration;
